<template>
  <div class="formulario-cadastro-container" :class="{ 'embedded-mode': isEmbedded }">
    <header class="formulario-header" v-if="!isEmbedded">
      <!-- Bloco do Título -->
      <div class="titulo-wrapper">
        <h4 class="titulo-principal">{{ moduleName }}</h4>
        <span class="titulo-acao">{{ isEditing ? 'Edição de registo' : 'Adição de novo registo' }}</span>

        <!-- Botão discreto para colapsar/expandir todos os frames (accordions) -->
        <button
          v-if="hasAccordion"
          type="button"
          class="btn-collapse-all"
          @click="toggleAllAccordions"
          :title="isAllCollapsed ? 'Expandir todos os frames' : 'Recolher todos os frames'"
        >
          <i :class="['bi', isAllCollapsed ? 'bi-arrows-angle-expand' : 'bi-arrows-angle-contract']"></i>
          <span>{{ isAllCollapsed ? 'Expandir seções' : 'Recolher seções' }}</span>
        </button>
      </div>

      <!-- Bloco de Informações do Registo (Nº Processo, extras, Status) -->
      <div v-if="isEditing && entityInfo" class="info-registro">
        <div class="info-item">
          <strong>{{ entityInfo.nameLabel }}:</strong> <span>{{ entityInfo.name }}</span>
        </div>
        <!-- Slot para linhas adicionais (ex: Nº Doc. Transporte) -->
        <slot name="info-header" />
        <!-- Slot para informações extras entre nome e status (ex: Operação) -->
        <slot name="info-extra" />
        <div v-if="status" class="info-item">
          <strong>Status:</strong> <span>{{ status }}</span>
        </div>
      </div>

      <!-- Bloco de Auditoria (Criado/Modificado por) -->
      <div v-if="isEditing && resolvedAuditInfo" class="audit-wrapper">
        <!-- Linha de Criação -->
        <div class="audit-item">
          <i class="bi bi-person-plus"></i>
          <span class="audit-label">Criado por</span>
          <span class="audit-user">{{ resolvedAuditInfo.nomeUsuarioCriacao || '—' }}</span>
          <span class="audit-date">{{ formatarDataHoraBR(resolvedAuditInfo.tsCriacao) }}</span>
        </div>
        <!-- Separador sutil entre criação e modificação -->
        <div v-if="temModificacao" class="audit-divider"></div>
        <!-- Linha de Modificação (apenas se tsModificacao > tsCriacao) -->
        <div v-if="temModificacao" class="audit-item">
          <i class="bi bi-pencil"></i>
          <span class="audit-label">Modificado por</span>
          <span class="audit-user">{{ resolvedAuditInfo.nomeUsuarioModificacao || '—' }}</span>
          <span class="audit-date">{{ formatarDataHoraBR(resolvedAuditInfo.tsModificacao) }}</span>
        </div>
      </div>

      <!-- Bloco dos Botões de Ação -->
      <div class="botoes-wrapper d-flex align-items-center">

        <!-- Slot dinâmico para injetar botões auxiliares (ex: Histórico de Log) -->
        <slot name="acoes-header"></slot>

        <button type="button" class="btn btn-outline-soft me-2" @click="handleCancel" :disabled="isLoading">
          <i class="bi bi-arrow-left me-1"></i>
          Cancelar
        </button>

        <!-- Botão: Salvar e Concluir (fecha o formulário após salvar) -->
        <button
          v-if="!ocultarSalvar"
          type="button"
          class="btn btn-primary me-2"
          @click="handleSubmit"
          :disabled="isLoading || desabilitarSalvar"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          <template v-if="!isLoading"><i class="bi bi-check2-circle me-1"></i></template>
          {{ isLoading ? 'Salvando...' : 'Salvar e Concluir' }}
        </button>

        <!-- Botão: Salvar Alterações (sem fechar o formulário) -->
        <!-- Só aparece se o componente-pai registrou @submit-continuar -->
        <button
          v-if="temSubmitContinuar && !ocultarSalvar"
          type="button"
          class="btn btn-soft-light"
          @click="handleSubmitContinuar"
          :disabled="isLoading || desabilitarSalvarAlteracoes"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          <template v-if="!isLoading"><i class="bi bi-floppy me-1"></i></template>
          {{ isLoading ? 'Salvando...' : 'Salvar Alterações' }}
        </button>
      </div>
    </header>

    <form @submit.prevent="handleSubmit" novalidate class="p-3">
      <!-- Container que controla a largura dos campos -->
      <div class="campos-container">
        <!-- O slot padrão para campos -->
        <slot></slot>
      </div>

      <!-- Slot para componentes que precisam de largura total -->
      <div class="full-width-container">
        <slot name="full-width"></slot>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, inject, useAttrs } from 'vue'
import { formatarDataHoraBR } from '@/utils/useDataBR'

// O compilador nativo do Vue 3 já entende o defineProps e defineEmits dentro do <script setup>.
const props = defineProps({
  moduleName: {
    type: String,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  entityInfo: {
    type: Object,
    default: () => ({ id: null, name: '', nameLabel: 'Nome' })
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  desabilitarSalvar: {
    type: Boolean,
    default: false
  },
  desabilitarSalvarAlteracoes: {
    type: Boolean,
    default: false
  },
  ocultarSalvar: {
    type: Boolean,
    default: false
  },
  isEmbedded: {
    type: Boolean,
    default: false
  },
  accordionState: {
    type: Object,
    default: null
  },
  status: {
    type: String,
    default: null
  },
  /**
   * Prop opcional de auditoria. Usada como fallback quando o componente-pai
   * NÃO usa o composable useFormulario (ex: EmbarquesMateriaisForm, EmbarquesValidacaoLpcoForm).
   * Estrutura esperada:
   *   { nomeUsuarioCriacao, tsCriacao, nomeUsuarioModificacao, tsModificacao }
   */
  auditInfo: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['submit', 'cancelar'])

// Detecta se o componente-pai registrou o listener @submit-continuar
// submit-continuar NÃO está no defineEmits para que o listener
// seja repassado via $attrs e detectável pelo useAttrs().
const attrs = useAttrs()
const temSubmitContinuar = computed(() => {
  return Boolean(attrs.onSubmitContinuar)
})

// GUARDIÃO: Informações de auditoria vindas de 3 fontes em ordem de precedência:
// 1. Prop explícita (passada pelo componente-pai sem useFormulario)
// 2. provide/inject do useFormulario (componentes que usam o composable)
// 3. Fallback: null (bloco não exibido)
const auditInfoInject = inject('auditInfo', null)
const resolvedAuditInfo = computed(() => {
  // GUARDIÃO: O inject de um computed() retorna o ComputedRef, não o valor.
  // Precisamos fazer .value para acessar os dados reais.
  const rawInject = auditInfoInject
  const rawInjectValue = rawInject && typeof rawInject === 'object' && 'value' in rawInject
    ? rawInject.value
    : rawInject
  const source = props.auditInfo || rawInjectValue || null
  if (!source) return null
  // Normaliza as chaves: o DTO do backend retorna PascalCase (NomeUsuarioCriacao)
  // enquanto o useFormulario já fornece camelCase (nomeUsuarioCriacao).
  return {
    nomeUsuarioCriacao: source.nomeUsuarioCriacao ?? source.NomeUsuarioCriacao ?? null,
    tsCriacao: source.tsCriacao ?? source.TsCriacao ?? null,
    nomeUsuarioModificacao: source.nomeUsuarioModificacao ?? source.NomeUsuarioModificacao ?? null,
    tsModificacao: source.tsModificacao ?? source.TsModificacao ?? null
  }
})

// Indica se o registro foi modificado.
// Um registro é considerado modificado quando:
// - tsModificacao existe E é estritamente maior que tsCriacao (com margem de 1s)
//   para evitar falso positivo quando ambos recebem DateTime.UtcNow no INSERT
const temModificacao = computed(() => {
  if (!resolvedAuditInfo.value) return false
  const info = resolvedAuditInfo.value
  const criacao = info.tsCriacao
  const modificacao = info.tsModificacao
  if (!modificacao) return false
  if (!criacao) return true
  // Compara timestamps: se modificação > criação (margem 1s), considera modificado
  const tsCriacao = new Date(criacao).getTime()
  const tsModificacao = new Date(modificacao).getTime()
  if (isNaN(tsCriacao) || isNaN(tsModificacao)) {
    return String(modificacao) !== String(criacao)
  }
  return (tsModificacao - tsCriacao) > 1000
})

const hasAccordion = computed(() => {
  return props.accordionState && typeof props.accordionState === 'object' && Object.keys(props.accordionState).length > 0
})

const isAllCollapsed = computed(() => {
  if (!hasAccordion.value) return false
  return Object.values(props.accordionState).every(val => val === false)
})

const toggleAllAccordions = () => {
  const targetState = isAllCollapsed.value
  // eslint-disable-next-line vue/no-mutating-props
  Object.keys(props.accordionState).forEach(key => {
    // eslint-disable-next-line vue/no-mutating-props
    props.accordionState[key] = targetState
  })
}

const handleSubmit = async () => {
  if (props.isLoading || props.desabilitarSalvar) {
    return
  }
  emit('submit')
}

const handleSubmitContinuar = async () => {
  if (props.isLoading || props.desabilitarSalvarAlteracoes) {
    return
  }
  emit('submit-continuar')
}

const handleCancel = () => {
  if (props.isLoading) {
    return
  }
  emit('cancelar')
}
</script>

<style scoped>
/* ==========================================================================
   GUARDIÃO: VARIÁVEIS DE TEMA PERSONALIZADAS
   ========================================================================== */
.formulario-cadastro-container {
  /* Cinza-azulado bem suave para o Light Mode (Mantendo a sua intenção original) */
  --ct-card-header-bg: rgba(52, 73, 94, 0.06);
}

:global([data-bs-theme="dark"]) .formulario-cadastro-container {
  /* Fundo escurecido para criar profundidade de "encrave" no cabeçalho em Dark Mode */
  --ct-card-header-bg: rgba(0, 0, 0, 0.15);
}

/* Modo Embutido (ex: dentro de um ModalFormulario) */
.formulario-cadastro-container.embedded-mode {
  border: none;
  border-radius: 0;
  box-shadow: none;
  background-color: transparent;
  margin: 0;
  min-height: auto;
}
.formulario-cadastro-container.embedded-mode .p-3 {
  padding: 0 !important;
}

/* Estilos do Cabeçalho Padrão (Desktop) */
.formulario-header {
  display: flex;
  height: 100px;
  align-items: center;
  gap: 40px;
  padding: 1.5rem;
  background-color: #34495E;
  border-bottom: 1px solid #1f2d3a;
  position: sticky;
  top: 0;
  z-index: 10;
}

.titulo-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.titulo-principal {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.1;
  color: #FFFFFF;
}

.titulo-acao {
  font-size: 0.875rem;
  color: #ADB5BD;
}

.btn-collapse-all {
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.5rem;
  color: rgba(173, 181, 189, 0.6);
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease-in-out;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.btn-collapse-all:hover {
  color: #FFFFFF;
}

.btn-collapse-all i {
  font-size: 0.85rem;
}

.info-registro {
  font-size: 0.9rem;
  line-height: 1.4;
}

:deep(.info-item) strong {
  color: #ADB5BD;
}

:deep(.info-item) span {
  color: #ca8019;
  font-weight: 600;
}

.botoes-wrapper {
  margin-left: auto;
  flex-shrink: 0;
}

/* ==========================================================================
   BOTÕES PERSONALIZADOS PARA HEADER ESCURO
   ========================================================================== */

/* Botão "Salvar Alterações" — estilo glass/translúcido sobre fundo escuro */
.btn-soft-light {
  background-color: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #FFFFFF;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.btn-soft-light:hover,
.btn-soft-light:focus-visible {
  background-color: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.30);
  color: #FFFFFF;
}
.btn-soft-light:active {
  background-color: rgba(255, 255, 255, 0.28) !important;
  border-color: rgba(255, 255, 255, 0.35) !important;
  color: #FFFFFF !important;
}
.btn-soft-light:disabled,
.btn-soft-light.disabled {
  background-color: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.4);
}

/* Botão "Cancelar" — outline suave, mais fraco que o Salvar Alterações */
.btn-outline-soft {
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.75);
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}
.btn-outline-soft:hover,
.btn-outline-soft:focus-visible {
  background-color: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.22);
  color: #FFFFFF;
}
.btn-outline-soft:active {
  background-color: rgba(255, 255, 255, 0.20) !important;
  border-color: rgba(255, 255, 255, 0.28) !important;
  color: #FFFFFF !important;
}
.btn-outline-soft:disabled,
.btn-outline-soft.disabled {
  background-color: transparent;
  border-color: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
}

/* ==========================================================================
   BLOCO DE AUDITORIA (Criado/Modificado por)
   ========================================================================== */
.audit-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.15rem;
  border-left: 1px solid rgba(255, 255, 255, 0.10);
  padding-left: 1.25rem;
  min-width: 180px;
}

.audit-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
}

.audit-item i {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.30);
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.audit-label {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.40);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 500;
  flex-shrink: 0;
}

.audit-user {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  white-space: nowrap;
}

.audit-date {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.45);
  margin-left: 0.15rem;
  white-space: nowrap;
}

.audit-divider {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin: 0.1rem 0;
}

/* Container dos campos */
.campos-container {
  margin-right: auto;
  margin-left: 0;
}

.full-width-container {
  padding: 1rem 0;
}

form {
  width: 100%;
}

/* ==========================================================================
   GUARDIÃO: BOTÃO DE AÇÕES HEADER E CSS GLOBAL DOS FRAMES (CARDS) DE CADASTRO
   ========================================================================== */

:deep(.btn-icon-minimalista) {
  background: none;
  border: none;
  padding: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.3rem;
  transition: color 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.btn-icon-minimalista:hover) {
  color: rgba(255, 255, 255, 1);
}

:deep(.card) {
  /* O Segredo do Contraste: var(--bs-tertiary-bg) é cinza-claro no Light Mode
     (destacando os inputs brancos) e cinza-elevado no Dark Mode */
  background-color: var(--bs-tertiary-bg);
  border-radius: 0.375rem;
  border: 1px solid var(--bs-border-color-translucent) !important;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.03);
  transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

:deep(.card-header) {
  background-color: var(--ct-card-header-bg);
  border-bottom: 1px solid var(--bs-border-color-translucent) !important;
  padding: 0.85rem 1.25rem;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

:deep(.card-header h5) {
  color: var(--bs-heading-color);
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
}

:deep(.card-body) {
  padding: 1.25rem;
}

/* --- AJUSTE: Media Query para Smartphone --- */
@media (max-width: 991.98px) {
  .formulario-header {
    position: static;
    height: auto;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  .titulo-wrapper { align-items: center; text-align: center; }
  .info-registro { display: flex; justify-content: center; gap: 1.5rem; text-align: center; }
  .audit-wrapper {
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.10);
    padding-left: 0;
    padding-top: 0.75rem;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
    min-width: 0;
  }
  .audit-divider {
    display: none;
  }
  .botoes-wrapper { margin-left: 0; display: flex; flex-direction: column; gap: 0.5rem; width: 100%; }
  .botoes-wrapper .btn { width: 100%; }
}
</style>
